import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { createEditor, Editor, Transforms, Element as SlateElement, Text as SlateText, Range } from 'slate';
import { Slate, Editable, withReact, useSlate } from 'slate-react';
import { X } from 'lucide-react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Quote,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Table,
  Link as LinkIcon
} from 'lucide-react';

// Helper function to convert HTML string to Slate value
const htmlToSlate = (html) => {
  if (!html || typeof html !== 'string' || html.trim() === '') {
    return [
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
    ];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  const parseNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      return text ? { text } : null;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const tagName = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes)
      .map(parseNode)
      .filter(Boolean)
      .flat();

    if (!children.length) {
      children.push({ text: '' });
    }

    switch (tagName) {
      case 'h1':
        return { type: 'heading-one', children };
      case 'h2':
        return { type: 'heading-two', children };
      case 'h3':
        return { type: 'heading-three', children };
      case 'h4':
        return { type: 'heading-one', children }; // Treat h4-h6 as h1 for simplicity
      case 'h5':
      case 'h6':
        return { type: 'heading-two', children };
      case 'blockquote':
        return { type: 'block-quote', children };
      case 'ul':
        return { type: 'bulleted-list', children };
      case 'ol':
        return { type: 'numbered-list', children };
      case 'li':
        return { type: 'list-item', children };
      case 'strong':
      case 'b':
        return children.map(child => ({ ...child, bold: true }));
      case 'em':
      case 'i':
        return children.map(child => ({ ...child, italic: true }));
      case 'u':
        return children.map(child => ({ ...child, underline: true }));
      case 'code':
        return children.map(child => ({ ...child, code: true }));
      case 'a':
        const url = node.getAttribute('href') || '';
        return children.map(child => ({ ...child, link: url }));
      case 'tbody':
        // Handle tbody - just return children
        return children;
      case 'table':
        // Find tbody or use direct tr children
        const tbody = node.querySelector('tbody') || node;
        const tableRows = Array.from(tbody.querySelectorAll('tr')).map(tr => {
          const cells = Array.from(tr.querySelectorAll('td, th')).map(cell => {
            const cellChildren = Array.from(cell.childNodes)
              .map(parseNode)
              .filter(Boolean)
              .flat();
            return {
              type: cell.tagName.toLowerCase() === 'th' ? 'table-header' : 'table-cell',
              children: cellChildren.length ? cellChildren : [{ text: '' }]
            };
          });
          return {
            type: 'table-row',
            children: cells.length ? cells : [{ type: 'table-cell', children: [{ text: '' }] }]
          };
        });
        return {
          type: 'table',
          children: tableRows.length ? tableRows : [{ type: 'table-row', children: [{ type: 'table-cell', children: [{ text: '' }] }] }]
        };
      case 'tr':
      case 'td':
      case 'th':
      case 'tbody':
        return null; // Handled in table case
      case 'p':
      case 'div':
      default:
        return { type: 'paragraph', children };
    }
  };

  const parsed = parseNode(body);
  return parsed?.children || [
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
  ];
};

// Helper function to convert Slate value to clean HTML string
const slateToHtml = (nodes) => {
  if (!Array.isArray(nodes)) {
    return '';
  }

  return nodes.map(node => {
    if (SlateText.isText(node)) {
      let text = escapeHtml(node.text);
      if (node.bold) text = `<strong>${text}</strong>`;
      if (node.italic) text = `<em>${text}</em>`;
      if (node.underline) text = `<u>${text}</u>`;
      if (node.code) text = `<code>${text}</code>`;
      if (node.link) {
        text = `<a href="${escapeHtml(node.link)}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
      return text;
    }

    const children = node.children.map(child => 
      SlateText.isText(child) ? slateToHtml([child]) : slateToHtml(child.children || [])
    ).join('');

    switch (node.type) {
      case 'heading-one':
        return `<h1>${children}</h1>`;
      case 'heading-two':
        return `<h2>${children}</h2>`;
      case 'heading-three':
        return `<h3>${children}</h3>`;
      case 'block-quote':
        return `<blockquote>${children}</blockquote>`;
      case 'bulleted-list':
        return `<ul>${children}</ul>`;
      case 'numbered-list':
        return `<ol>${children}</ol>`;
      case 'list-item':
        return `<li>${children}</li>`;
      case 'table':
        return `<table style="border-collapse: collapse; width: 100%; margin: 1rem 0;"><tbody>${children}</tbody></table>`;
      case 'table-row':
        return `<tr>${children}</tr>`;
      case 'table-header':
        return `<th style="border: 1px solid #ccc; padding: 8px 12px; background-color: #f9fafb; font-weight: 600; text-align: left;">${children}</th>`;
      case 'table-cell':
        return `<td style="border: 1px solid #ccc; padding: 8px 12px;">${children}</td>`;
      case 'paragraph':
        return `<p>${children}</p>`;
      default:
        return `<p>${children}</p>`;
    }
  }).join('');
};

const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Toolbar button component
const ToolbarButton = ({ icon: Icon, isActive, onClick, label }) => (
  <button
    type="button"
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    className={`
      p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors
      ${isActive ? 'bg-gray-300 dark:bg-gray-600 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}
    `}
    title={label}
    aria-label={label}
  >
    <Icon size={18} />
  </button>
);

// Format button component
const FormatButton = ({ format, icon: Icon, label }) => {
  const editor = useSlate();
  const isActive = isFormatActive(editor, format);
  
  return (
    <ToolbarButton
      icon={Icon}
      isActive={isActive}
      onClick={() => toggleFormat(editor, format)}
      label={label}
    />
  );
};

// Block button component
const BlockButton = ({ format, icon: Icon, label }) => {
  const editor = useSlate();
  const isActive = isBlockActive(editor, format);
  
  return (
    <ToolbarButton
      icon={Icon}
      isActive={isActive}
      onClick={() => toggleBlock(editor, format)}
      label={label}
    />
  );
};

// Check if a format is active
const isFormatActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

// Check if link is active
const isLinkActive = (editor) => {
  const marks = Editor.marks(editor);
  return marks && marks.link;
};

// Toggle a format
const toggleFormat = (editor, format) => {
  const isActive = isFormatActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

// Insert or update link
const insertLink = (editor, url) => {
  if (!url) {
    Editor.removeMark(editor, 'link');
    return;
  }
  
  const { selection } = editor;
  if (selection) {
    const isCollapsed = Range.isCollapsed(selection);
    
    if (isCollapsed) {
      // If no text selected, insert link text
      const linkText = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
      Transforms.insertText(editor, linkText);
      Transforms.select(editor, {
        anchor: { path: selection.anchor.path, offset: selection.anchor.offset },
        focus: { path: selection.anchor.path, offset: selection.anchor.offset + linkText.length }
      });
    }
    
    Editor.addMark(editor, 'link', url);
  }
};

// Remove link
const removeLink = (editor) => {
  Editor.removeMark(editor, 'link');
};

// Check if a block type is active
const isBlockActive = (editor, format) => {
  const { selection } = editor;
  if (!selection) return false;
  
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n.type === format,
    })
  );
  
  return !!match;
};

// Insert a table
const insertTable = (editor, rows = 3, cols = 3) => {
  const table = {
    type: 'table',
    children: Array.from({ length: rows }).map((_, rowIdx) => ({
      type: 'table-row',
      children: Array.from({ length: cols }).map(() => ({
        type: rowIdx === 0 ? 'table-header' : 'table-cell',
        children: [{ text: '' }],
      })),
    })),
  };
  
  Transforms.insertNodes(editor, table);
};

// Toggle a block type
const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format);
  const isList = ['numbered-list', 'bulleted-list'].includes(format);
  
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      ['numbered-list', 'bulleted-list'].includes(n.type),
    split: true,
  });
  
  let newProperties;
  if (isActive) {
    newProperties = { type: 'paragraph' };
  } else if (isList) {
    newProperties = {
      type: 'list-item',
    };
    Transforms.wrapNodes(editor, {
      type: format,
      children: [],
    });
  } else {
    newProperties = { type: format };
  }
  
  Transforms.setNodes(editor, newProperties);
  
  if (isList && !isActive) {
    const block = { type: 'list-item', children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

// Default element renderer
const DefaultElement = ({ attributes, children }) => {
  return <p {...attributes} className="mb-2">{children}</p>;
};

// Leaf renderer
const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  if (leaf.code) {
    children = <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm">{children}</code>;
  }
  if (leaf.link) {
    children = (
      <a href={leaf.link} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
        {children}
      </a>
    );
  }
  
  return <span {...attributes}>{children}</span>;
};

// Link insertion modal component
const LinkModal = ({ isOpen, onClose, onConfirm, initialUrl = '' }) => {
  const [url, setUrl] = useState(initialUrl);

  useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl);
    }
  }, [isOpen, initialUrl]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (url.trim()) {
      // Ensure URL has protocol
      let finalUrl = url.trim();
      if (!finalUrl.match(/^https?:\/\//)) {
        finalUrl = 'https://' + finalUrl;
      }
      onConfirm(finalUrl);
      setUrl('');
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-heading dark:text-white">
            Insert Link
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              URL
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyPress}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
              placeholder="https://example.com"
              autoFocus
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Enter the URL to link to
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-accent hover:bg-accent-dark text-white rounded-lg transition-colors"
          >
            Insert Link
          </button>
        </div>
      </div>
    </div>
  );
};

// Table insertion modal component
const TableModal = ({ isOpen, onClose, onConfirm }) => {
  const [rows, setRows] = useState('3');
  const [cols, setCols] = useState('3');

  if (!isOpen) return null;

  const handleConfirm = () => {
    const rowsNum = parseInt(rows) || 3;
    const colsNum = parseInt(cols) || 3;
    if (rowsNum > 0 && colsNum > 0 && rowsNum <= 20 && colsNum <= 20) {
      onConfirm(rowsNum, colsNum);
      setRows('3');
      setCols('3');
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-heading dark:text-white">
            Insert Table
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Number of Rows
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={rows}
              onChange={(e) => setRows(e.target.value)}
              onKeyDown={handleKeyPress}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
              placeholder="3"
              autoFocus
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Enter a number between 1 and 20
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Number of Columns
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={cols}
              onChange={(e) => setCols(e.target.value)}
              onKeyDown={handleKeyPress}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
              placeholder="3"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Enter a number between 1 and 20
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-accent hover:bg-accent-dark text-white rounded-lg transition-colors"
          >
            Insert Table
          </button>
        </div>
      </div>
    </div>
  );
};

// Link button component
const LinkButton = () => {
  const editor = useSlate();
  const [showModal, setShowModal] = useState(false);
  const isActive = isLinkActive(editor);
  
  const handleInsertLink = (url) => {
    insertLink(editor, url);
  };
  
  const handleRemoveLink = () => {
    removeLink(editor);
  };
  
  const handleClick = () => {
    if (isActive) {
      handleRemoveLink();
    } else {
      const marks = Editor.marks(editor);
      const currentUrl = marks?.link || '';
      setShowModal(true);
    }
  };
  
  return (
    <>
      <ToolbarButton
        icon={LinkIcon}
        isActive={isActive}
        onClick={handleClick}
        label={isActive ? "Remove Link" : "Insert Link"}
      />
      <LinkModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleInsertLink}
        initialUrl={Editor.marks(editor)?.link || ''}
      />
    </>
  );
};

// Table button component
const TableButton = () => {
  const editor = useSlate();
  const [showModal, setShowModal] = useState(false);
  
  const handleInsertTable = (rows, cols) => {
    insertTable(editor, rows, cols);
  };
  
  return (
    <>
      <ToolbarButton
        icon={Table}
        isActive={false}
        onClick={() => setShowModal(true)}
        label="Insert Table"
      />
      <TableModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleInsertTable}
      />
    </>
  );
};

// Toolbar component
const Toolbar = () => (
  <div className="flex flex-wrap gap-1 p-2 border-b border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 rounded-t-lg">
    <BlockButton format="heading-one" icon={Heading1} label="Heading 1" />
    <BlockButton format="heading-two" icon={Heading2} label="Heading 2" />
    <BlockButton format="heading-three" icon={Heading3} label="Heading 3" />
    
    <div className="w-px h-6 bg-gray-300 dark:border-gray-600 mx-1" />
    
    <FormatButton format="bold" icon={Bold} label="Bold" />
    <FormatButton format="italic" icon={Italic} label="Italic" />
    <FormatButton format="underline" icon={Underline} label="Underline" />
    <FormatButton format="code" icon={Code} label="Code" />
    <LinkButton />
    
    <div className="w-px h-6 bg-gray-300 dark:border-gray-600 mx-1" />
    
    <BlockButton format="numbered-list" icon={ListOrdered} label="Numbered List" />
    <BlockButton format="bulleted-list" icon={List} label="Bulleted List" />
    <BlockButton format="block-quote" icon={Quote} label="Quote" />
    <TableButton />
  </div>
);

// Main ProfessionalRichTextEditor component
const ProfessionalRichTextEditor = ({ value, onChange, placeholder = "Enter content..." }) => {
  const editor = useMemo(() => withReact(createEditor()), []);
  
  // Initialize with HTML string converted to Slate value
  const [slateValue, setSlateValue] = useState(() => {
    return htmlToSlate(value || '');
  });

  // Update Slate value when external value changes (for edit mode)
  useEffect(() => {
    if (value !== undefined && value !== null) {
      const newValue = htmlToSlate(value);
      const currentHtml = slateToHtml(slateValue);
      if (currentHtml !== value && value !== '') {
        setSlateValue(newValue);
      }
    }
  }, [value]);

  // Handle value change - convert Slate value to HTML and call onChange
  const handleChange = useCallback((val) => {
    setSlateValue(val);
    const htmlString = slateToHtml(val);
    onChange(htmlString);
  }, [onChange]);
  
  // Render element based on type
  const renderElement = useCallback((props) => {
    switch (props.element.type) {
      case 'heading-one':
        return <h1 {...props.attributes} className="text-3xl font-bold mb-2 mt-4">{props.children}</h1>;
      case 'heading-two':
        return <h2 {...props.attributes} className="text-2xl font-bold mb-2 mt-4">{props.children}</h2>;
      case 'heading-three':
        return <h3 {...props.attributes} className="text-xl font-bold mb-2 mt-4">{props.children}</h3>;
      case 'block-quote':
        return (
          <blockquote {...props.attributes} className="border-l-4 border-accent pl-4 italic my-2 bg-gray-50 dark:bg-gray-800 py-2">
            {props.children}
          </blockquote>
        );
      case 'numbered-list':
        return <ol {...props.attributes} className="list-decimal list-inside my-2 space-y-1 ml-4">{props.children}</ol>;
      case 'bulleted-list':
        return <ul {...props.attributes} className="list-disc list-inside my-2 space-y-1 ml-4">{props.children}</ul>;
      case 'list-item':
        return <li {...props.attributes}>{props.children}</li>;
      case 'table':
        return (
          <div {...props.attributes} className="my-4 overflow-x-auto">
            <table className="border-collapse border border-gray-300 dark:border-gray-600 w-full">
              <tbody>{props.children}</tbody>
            </table>
          </div>
        );
      case 'table-row':
        return <tr {...props.attributes}>{props.children}</tr>;
      case 'table-header':
        return (
          <th {...props.attributes} className="border border-gray-300 dark:border-gray-600 px-4 py-2 bg-gray-100 dark:bg-gray-700 font-semibold text-left">
            {props.children}
          </th>
        );
      case 'table-cell':
        return (
          <td {...props.attributes} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
            {props.children}
          </td>
        );
      default:
        return <DefaultElement {...props} />;
    }
  }, []);
  
  // Render leaf based on format
  const renderLeaf = useCallback((props) => {
    return <Leaf {...props} />;
  }, []);
  
  return (
    <div className="professional-rich-text-editor border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
      <Slate editor={editor} initialValue={slateValue} onChange={handleChange}>
        <Toolbar />
        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={placeholder}
          className="min-h-[200px] p-4 focus:outline-none prose dark:prose-invert max-w-none"
        />
      </Slate>
    </div>
  );
};

export default ProfessionalRichTextEditor;
